/**
 * Pacote 3 · P3-11 (M3 aprovado): clonar a Oportunidade. Cria uma NOVA Oportunidade (numeração própria) e,
 * opcionalmente, copia os itens da Proposta vigente para uma NOVA Proposta em rascunho. A origem não muda.
 * A prévia vem do servidor: o que copia, o que precisa de nova confirmação e o que não é copiado.
 */
import { useState } from 'react';
import { api } from '../api.ts';
import { navegar } from '../rotas.ts';
import { Icone } from '../componentes/icones.tsx';
import { Ajuda, Estado, EstadoCarga, Modal, useCarga } from './ui.tsx';
import type { Oportunidade } from './tiposOportunidade.ts';

interface Previa {
  proposta: { rotulo: string; itens: number } | null;
  copia: string[];
  reconfirmar: string[];
  naoCopia: string[];
}

export function ModalClonarOportunidade({ o, responsaveis, aoFechar }: { o: Oportunidade; responsaveis: { id: string; nome: string }[]; aoFechar: () => void }) {
  const previa = useCarga(() => api.get<Previa>(`/api/oportunidades/${o.id}/clonar`), [o.id]);
  const ativas = o.entidades_cliente.filter((e) => e.status === 'ativo');
  const [titulo, setTitulo] = useState(`${o.titulo} (cópia de #${o.numero})`);
  const [entidadeId, setEntidadeId] = useState(ativas.some((e) => e.id === o.entidade.id) ? o.entidade.id : '');
  const [responsavel, setResponsavel] = useState(o.responsavel.id);
  const [copiar, setCopiar] = useState(true);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const unidade = ativas.find((e) => e.id === entidadeId);
  const pronto = Boolean(titulo.trim() && entidadeId && responsavel);
  const criar = async () => {
    if (!pronto || salvando) return;
    setSalvando(true);
    setErro('');
    try {
      const r = await api.post<{ id: string }>(`/api/oportunidades/${o.id}/clonar`, { titulo: titulo.trim(), entidade_id: entidadeId, responsavel_id: responsavel, copiar_itens: Boolean(previa.dados?.proposta) && copiar });
      aoFechar();
      navegar(`/oportunidades/${r.id}`);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal
      titulo={`Clonar a Oportunidade #${o.numero}`}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={!pronto || salvando || !previa.dados} aria-busy={salvando} onClick={criar} data-teste="criar-copia">
            Criar cópia
          </button>
        </>
      }
    >
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-clonagem">
          {erro}
        </p>
      )}
      <div className="duas-colunas-clonagem">
        <div className="coluna-clonagem">
          <label className="campo">
            Título *
            <input value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={200} data-teste="titulo-copia" />
          </label>
          <div className="par-confirmacao">
            <span className="rotulo-campo">Cliente</span>
            <span>
              {o.cliente.nome} ({o.cliente.codigo})
            </span>
          </div>
          <div className="bloco-unidade" data-teste="unidade-copia">
            <label className="cab-unidade" htmlFor="unidade-copia">
              <Icone nome="clientes" /> Filial/unidade atendida *
            </label>
            <select id="unidade-copia" value={entidadeId} onChange={(e) => setEntidadeId(e.target.value)} data-teste="entidade-copia">
              {!entidadeId && <option value="">Escolha a unidade</option>}
              {ativas.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.razao_social} — {e.cnpj}
                </option>
              ))}
            </select>
            {unidade && (
              <div className="resumo-unidade">
                <span>
                  CNPJ <b>{unidade.cnpj}</b>
                </span>
                {unidade.codigo && (
                  <span>
                    Código <b>{unidade.codigo}</b>
                  </span>
                )}
              </div>
            )}
          </div>
          <label className="campo">
            Responsável *
            <select value={responsavel} onChange={(e) => setResponsavel(e.target.value)} data-teste="responsavel-copia">
              {responsaveis.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nome}
                </option>
              ))}
            </select>
          </label>
          {previa.dados?.proposta && (
            <label className="opcao">
              <input type="checkbox" checked={copiar} onChange={(e) => setCopiar(e.target.checked)} data-teste="copiar-itens" /> Copiar os itens para uma nova Proposta em rascunho
            </label>
          )}
        </div>
        <div className="bloco-previa-clonagem" data-teste="previa-clonagem">
          <h3>
            Prévia da cópia<Ajuda titulo="Prévia da cópia">A Oportunidade de origem não muda. A Timeline das duas registra a clonagem. Itens incluídos depois na cópia nascem com a margem desejada padrão (7%).</Ajuda>
          </h3>
          {!previa.dados ? (
            <EstadoCarga carregando={previa.carregando} erro={previa.erro} recarregar={previa.recarregar} teste="previa-clonagem" />
          ) : (
            <>
              <div className="secao-previa">
                <span className="selo-previa ok">Copia</span>
                <ul>{previa.dados.copia.filter((x) => copiar || !/item\(ns\)/.test(x)).map((x) => <li key={x}>{x}</li>)}</ul>
              </div>
              {copiar && previa.dados.reconfirmar.length > 0 && (
                <div className="secao-previa">
                  <span className="selo-previa pend">Confirmar antes de enviar</span>
                  <ul>{previa.dados.reconfirmar.map((x) => <li key={x}>{x}</li>)}</ul>
                </div>
              )}
              <div className="secao-previa">
                <span className="selo-previa ni">Não copia</span>
                <ul>{previa.dados.naoCopia.map((x) => <li key={x}>{x}</li>)}</ul>
              </div>
              {!previa.dados.proposta && <Estado tipo="vazio" titulo="a origem não tem Proposta: só os dados da Oportunidade são copiados" />}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
